import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      // Only fetch notifications if user is authenticated
      if (!token) {
        setNotifications([]);
        setUnreadCount(0);
        return;
      }

      setLoading(true);
      const response = await axios.get('/api/notifications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setNotifications(list);
      // Get userId from user object stored in localStorage (user.id, not userId)
      const userData = localStorage.getItem('user');
      const userId = userData ? JSON.parse(userData).id : null;
      const headerUnread = response.headers?.['x-notifications-unread-count'];
      const unreadFromHeader = headerUnread != null && headerUnread !== ''
        ? Number(headerUnread)
        : null;
      const unread = Number.isFinite(unreadFromHeader)
        ? unreadFromHeader
        : list.filter(n => !n.readBy?.some(r => String(r.userId) === String(userId))).length;
      setUnreadCount(unread);
    } catch (error) {
      // Only log error if it's not a 401 (unauthorized) - this is expected when not logged in
      if (error.response?.status !== 401) {
        console.error('Failed to fetch notifications:', error);
      }
      // Clear notifications on auth error
      if (error.response?.status === 401) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } finally {
      setLoading(false);
    }
  };

  // Track pending requests and timeouts to prevent duplicate calls
  const pendingReadRequests = useRef(new Set());
  const readRequestTimeouts = useRef(new Map());

  const markAsRead = async (notificationId) => {
    try {
      // Get userId from user object stored in localStorage (user.id, not userId)
      const userData = localStorage.getItem('user');
      const userId = userData ? JSON.parse(userData).id : null;
      const token = localStorage.getItem('token');
      
      // Check if already read locally to avoid unnecessary requests
      const notification = notifications.find(n => n._id === notificationId);
      if (notification?.readBy?.some(r => String(r.userId) === String(userId))) {
        return; // Already marked as read
      }

      // Check if request is already pending
      if (pendingReadRequests.current.has(notificationId)) {
        return; // Request already in progress
      }

      // Clear any existing timeout for this notification
      const existingTimeout = readRequestTimeouts.current.get(notificationId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        readRequestTimeouts.current.delete(notificationId);
      }

      // Debounce: wait 300ms before making the request (allows batching rapid clicks)
      const timeoutId = setTimeout(async () => {
        pendingReadRequests.current.add(notificationId);
        readRequestTimeouts.current.delete(notificationId);
        
        try {
      await axios.post(`/api/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
          
          // Update local state optimistically
      setNotifications(prev => 
        prev.map(n => 
          n._id === notificationId 
                ? { ...n, readBy: [...(n.readBy || []), { userId: userId, at: new Date() }] }
            : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
          // Only log if it's not a 429 (rate limit) or 404 (already handled)
          if (error.response?.status !== 429 && error.response?.status !== 404) {
            console.error('Failed to mark notification as read:', error);
          }
        } finally {
          pendingReadRequests.current.delete(notificationId);
        }
      }, 300);
      
      readRequestTimeouts.current.set(notificationId, timeoutId);
    } catch (error) {
      pendingReadRequests.current.delete(notificationId);
      const timeoutId = readRequestTimeouts.current.get(notificationId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        readRequestTimeouts.current.delete(notificationId);
      }
      if (error.response?.status !== 429) {
      console.error('Failed to mark notification as read:', error);
      }
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      // Get userId from user object stored in localStorage (user.id, not userId)
      const userData = localStorage.getItem('user');
      const userId = userData ? JSON.parse(userData).id : null;
      
      // Use the batch endpoint to mark all as read in a single request
      await axios.post('/api/notifications/read-all', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state - mark all unread notifications as read
      setNotifications(prev => 
        prev.map(n => {
          const isRead = n.readBy?.some(r => String(r.userId) === String(userId));
          if (!isRead) {
            return {
              ...n,
              readBy: [...(n.readBy || []), { userId: userId, at: new Date() }]
            };
          }
          return n;
        })
      );
      setUnreadCount(0);
    } catch (error) {
      // Only log if it's not a 429 (rate limit)
      if (error.response?.status !== 429) {
      console.error('Failed to mark all as read:', error);
      }
    }
  };

  const clearAll = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/notifications/clear-all', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      if (error.response?.status !== 429) {
        console.error('Failed to clear notifications:', error);
      }
    }
  };

  useEffect(() => {
    // Only fetch notifications if user is authenticated (has token)
    const token = localStorage.getItem('token');
    if (token) {
      fetchNotifications();
    }
  }, []);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearAll
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
